/**
 * The three drop regions, the six photo anchors, and where a freshly enabled
 * field lands (spec R38, R48, R52).
 */

import {
  BlockAnchor,
  FieldId,
  FloatingAnchor,
  PhotoAnchor,
  RegionId,
} from '../types';

export interface RegionDefinition {
  id: RegionId;
  /**
   * The region's name, exactly as R48 words it. One string, read by both the
   * preview's region header and the properties panel's region line — so the two
   * cannot drift apart the next time either is edited.
   */
  name: string;
}

/** R48 — these three labels, verbatim. */
export const REGIONS: RegionDefinition[] = [
  { id: 'photo', name: 'On the photo' },
  { id: 'info', name: 'Product info' },
  { id: 'floating', name: 'Footer' },
];

export const getRegionName = (region: RegionId): string =>
  REGIONS.find((candidate) => candidate.id === region)?.name ?? '';

export interface PhotoAnchorDefinition {
  id: PhotoAnchor;
  /** Short label rendered on the snap target. */
  label: string;
}

export const PHOTO_ANCHORS: PhotoAnchorDefinition[] = [
  { id: 'top-left', label: 'TL' },
  { id: 'top-right', label: 'TR' },
  { id: 'bottom-left', label: 'BL' },
  { id: 'bottom-right', label: 'BR' },
  { id: 'center', label: 'Center' },
  { id: 'bottom-strip', label: 'Bottom strip' },
];

export const DEFAULT_PHOTO_ANCHOR: PhotoAnchor = 'top-left';

export const FLOATING_ANCHOR: FloatingAnchor = 'sticky';

export const isPhotoAnchor = (candidate: unknown): candidate is PhotoAnchor =>
  PHOTO_ANCHORS.some((anchor) => anchor.id === candidate);

export const isRegionId = (candidate: unknown): candidate is RegionId =>
  REGIONS.some((region) => region.id === candidate);

export interface SuggestedPlacement {
  region: RegionId;
  anchor?: BlockAnchor;
}

/**
 * Only fields that visually make sense as overlays or pins get a non-Info
 * suggestion (spec R38). Everything else stacks in Info, which keeps the
 * at-rest card clean; the user can still drag it anywhere.
 */
export const SUGGESTED_REGION: Partial<Record<FieldId, SuggestedPlacement>> = {
  badge: { region: 'photo', anchor: 'top-left' },
  newArrival: { region: 'photo', anchor: 'top-left' },
  bestSeller: { region: 'photo', anchor: 'top-left' },
  promoMsg: { region: 'photo', anchor: 'bottom-strip' },
  sponsored: { region: 'floating', anchor: FLOATING_ANCHOR },
};

export const DEFAULT_SUGGESTED_PLACEMENT: SuggestedPlacement = { region: 'info' };

export const getSuggestedPlacement = (fieldId: FieldId): SuggestedPlacement =>
  SUGGESTED_REGION[fieldId] ?? DEFAULT_SUGGESTED_PLACEMENT;
